import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Tracked values are namespaced by group so unrelated columns can be tracked
 * side by side without colliding: a PRO and a trouble-ticket number can share
 * the same digits, and each group keeps its OWN "last clicked" ring (clicking a
 * ticket must not move the ring off the PRO). Callers that omit the group get
 * the default PRO namespace.
 */
export const DEFAULT_VIEWED_GROUP = 'pro';
/** Namespace for the light blue tracked slot (TanTable's trackViewedBlueColumnId). */
export const VIEWED_BLUE_GROUP = 'blue';

interface ViewedProsContextType {
  isViewed: (pro: number | string | null | undefined, group?: string) => boolean;
  isLastViewed: (pro: number | string | null | undefined, group?: string) => boolean;
  markViewed: (pro: number | string | null | undefined, group?: string) => void;
}

const ViewedProsContext = createContext<ViewedProsContextType | null>(null);

const normalize = (pro: number | string | null | undefined): string => {
  if (pro === null || pro === undefined) return '';
  return String(pro).trim();
};

export const ViewedProsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // group -> set of clicked values, and group -> most recently clicked value.
  const [viewed, setViewed] = useState<Record<string, Set<string>>>(() => ({}));
  const [lastViewed, setLastViewed] = useState<Record<string, string>>(() => ({}));

  const markViewed = useCallback((pro: number | string | null | undefined, group = DEFAULT_VIEWED_GROUP) => {
    const key = normalize(pro);
    if (!key) return;
    setLastViewed((prev) => (prev[group] === key ? prev : { ...prev, [group]: key }));
    setViewed((prev) => {
      const set = prev[group];
      if (set?.has(key)) return prev;
      const next = new Set(set ?? []);
      next.add(key);
      return { ...prev, [group]: next };
    });
  }, []);

  const isViewed = useCallback(
    (pro: number | string | null | undefined, group = DEFAULT_VIEWED_GROUP): boolean => {
      const key = normalize(pro);
      if (!key) return false;
      return viewed[group]?.has(key) ?? false;
    },
    [viewed],
  );

  const isLastViewed = useCallback(
    (pro: number | string | null | undefined, group = DEFAULT_VIEWED_GROUP): boolean => {
      const key = normalize(pro);
      if (!key) return false;
      return lastViewed[group] === key;
    },
    [lastViewed],
  );

  return (
    <ViewedProsContext.Provider value={{ isViewed, isLastViewed, markViewed }}>
      {children}
    </ViewedProsContext.Provider>
  );
};

const NOOP_VIEWED_PROS: ViewedProsContextType = {
  isViewed: () => false,
  isLastViewed: () => false,
  markViewed: () => {},
};

export const useViewedPros = (): ViewedProsContextType => {
  return useContext(ViewedProsContext) ?? NOOP_VIEWED_PROS;
};
